import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DragDropProvider } from '@dnd-kit/react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { Spinner } from '@hsl-fi/loading-indicators';
import { Modal, ModalContent } from '@hsl-fi/dialog';
import DialogModal from '@digitransit-component/digitransit-component-dialog-modal';
import { FlexColumn, Text, FlexRow } from '@hsl-fi/layout-primitives';
import { useSortable } from '@dnd-kit/react/sortable';
import Icon from '@digitransit-component/digitransit-component-icon';
import 'lodash/uniqWith';
import memoize from 'lodash/memoize';
import escapeRegExp from 'lodash/escapeRegExp';
import cloneDeep from 'lodash/cloneDeep';
import { createInstance } from 'i18next';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

/**
 * Location properties.
 * @typedef {LocationProperties}
 * @property {string} layer Suggestion layer.
 * @property {lat} [number]
 * @property {lon} [number]
 * @property {string} address Address.
 */

/**
 * Returns localadmin (city name) for suggestions
 *
 * @name getLocaladmin
 * @param {Object} suggestion suggestion's properties from geocoding or a favourite stop/station.
 * Expects last part of an address (after ',') to contain the city name of the suggestion location.
 * @returns {String}  City name or empty string
 */
const getLocaladmin = suggestion => suggestion.localadmin || suggestion.localadmin || suggestion.address && suggestion.address.lastIndexOf(',') < suggestion.address.length - 2 && suggestion.address.substring(suggestion.address.lastIndexOf(',') + 2) || '';
const getStopCode = ({
  id,
  code
}) => {
  if (code) {
    return code;
  }
  if (id === undefined || typeof id.indexOf === 'undefined' || id.indexOf('#') === -1) {
    return undefined;
  }
  // id from pelias
  return id.substring(id.indexOf('#') + 1);
};
const formatFavouritePlaceLabel = (name, address) => [name || address && address.split(',')[0], typeof address === 'string' ? address.replace(new RegExp(`${escapeRegExp(name)}([ ,]|$)+`), '') : ''];

/**
 * Format suggestion-pair [name, address] by `suggestion.layer`.
 *
 * @name getNameLabel
 * @param {LocationProperties} suggestion
 * @param {boolean} [plain]
 *
 * @returns {Array.<string>} Formatted label.
 */
memoize((suggestion, plain = false) => {
  switch (suggestion.layer) {
    case 'currentPosition':
      return [suggestion.labelId, suggestion.address];
    case 'selectFromMap':
    case 'ownLocations':
    case 'back':
      return [suggestion.labelId];
    case 'favouritePlace':
      return formatFavouritePlaceLabel(suggestion.name, suggestion.address);
    case 'favouriteVehicleRentalStation':
    case 'bikestation':
      return [suggestion.name];
    case 'favouriteRoute':
    case 'route-BUS':
    case 'route-TRAM':
    case 'route-RAIL':
    case 'route-SUBWAY':
    case 'route-FERRY':
    case 'route-FUNICULAR':
    case 'route-AIRPLANE':
      return !plain && suggestion.shortName ? [suggestion.gtfsId, suggestion.mode.toLowerCase(), suggestion.longName] : [suggestion.shortName, suggestion.longName, suggestion.agency ? suggestion.agency.name : undefined];
    case 'venue':
    case 'address':
    case 'street':
      return [suggestion.name, suggestion.label.replace(new RegExp(`${escapeRegExp(suggestion.name)}(,)?( )?`), '')];
    case 'favouriteStop':
    case 'stop':
      return plain ? [suggestion.name || suggestion.label || suggestion.address && suggestion.address.split(',')[0], getLocaladmin(suggestion)] : [suggestion.name, suggestion.id, getStopCode(suggestion), getLocaladmin(suggestion)];
    case 'favouriteStation':
    case 'station':
    default:
      return [suggestion.name || suggestion.label || suggestion.address && suggestion.address.split(',')[0], getLocaladmin(suggestion)];
  }
}, (item, plain) => {
  const i = cloneDeep(item);
  i.plain = plain;
  return i;
});

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".styles_modalContent__DPecW.styles_modalContent__DPecW:focus, .styles_modalContent__DPecW.styles_modalContent__DPecW:focus-visible {\n  outline: none;\n}\n\n.styles_listContainer__BE6HY {\n  position: relative;\n  width: 100%;\n}\n@media (max-width: 499px) {\n  .styles_listContainer__BE6HY {\n    margin-top: 24px;\n  }\n}\n@media (min-width: 500px) {\n  .styles_listContainer__BE6HY {\n    min-height: 286px;\n  }\n}\n\n.styles_list__xeRy9 {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-fixed-xs, 8px);\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n\n.styles_item__DbRsI {\n  display: flex;\n  align-items: center;\n  min-height: 50px;\n  background: var(--color-surface-default, #fff);\n  border: 1px solid var(--color-border-weak, #ddd);\n  border-radius: var(--radius-m, 8px);\n  box-sizing: border-box;\n}\n\n.styles_dragHandle__kplvR {\n  display: flex;\n  align-items: center;\n  flex: none;\n  align-self: stretch;\n  cursor: move;\n  color: var(--color-icon-accent, #0074bf);\n}\n\n.styles_ellipsis__Gma55 {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 37px;\n  color: var(--color-icon-weakest, #888);\n}\n.styles_ellipsis__Gma55 svg {\n  height: 20px;\n  transform: rotate(90deg);\n}\n\n.styles_placeIcon__-6soL {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 24px;\n  margin-right: var(--space-fixed-xs, 8px);\n}\n.styles_placeIcon__-6soL svg {\n  width: 20px;\n  height: 20px;\n}\n\n.styles_arrow__lYXD3:not(:focus, :active) {\n  clip: rect(0 0 0 0);\n  clip-path: inset(50%);\n  height: 1px;\n  overflow: hidden;\n  position: absolute;\n  white-space: nowrap;\n  width: 1px;\n}\n\n.styles_arrow__lYXD3 {\n  font-weight: var(--font-weight-medium, 500);\n}\n\n.styles_text__al7Br {\n  flex: 1 1 0;\n  min-width: 0;\n  padding-left: var(--space-fixed-xs, 8px);\n}\n\n.styles_name__en71y,\n.styles_address__-cd84 {\n  margin: 0;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.styles_address__-cd84:empty {\n  display: none;\n}\n\n.styles_actionButton__y-oae {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 44px;\n  height: 48px;\n  margin: 0;\n  padding: 0;\n  border: 0;\n  background: none;\n  cursor: pointer;\n  color: var(--color-icon-weakest, #888);\n}\n.styles_actionButton__y-oae:hover, .styles_actionButton__y-oae:focus-visible {\n  color: var(--color-icon-accent, #0074bf);\n}\n.styles_actionButton__y-oae svg {\n  width: 18px;\n  height: 18px;\n}\n\n.styles_overlay__TtXTn {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  position: absolute;\n  inset: 0;\n  background: var(--color-surface-default, #fff);\n}";
var styles = {"modalContent":"styles_modalContent__DPecW","listContainer":"styles_listContainer__BE6HY","list":"styles_list__xeRy9","item":"styles_item__DbRsI","dragHandle":"styles_dragHandle__kplvR","ellipsis":"styles_ellipsis__Gma55","placeIcon":"styles_placeIcon__-6soL","arrow":"styles_arrow__lYXD3","text":"styles_text__al7Br","name":"styles_name__en71y","address":"styles_address__-cd84","actionButton":"styles_actionButton__y-oae","overlay":"styles_overlay__TtXTn"};
styleInject(css_248z);

/* eslint sort-keys: "error" */
const translations = {
  de: {
    translation: {
      cancel: 'Abbrechen',
      delete: 'Löschen',
      'delete-place-header': 'Wollen Sie den Favoriten löschen?',
      'delete-place-name': 'Lösche Favorit: {{favourite.name}}',
      'edit-place-name': 'Bearbeite Favorit: {{favourite.name}}',
      'edit-places': 'Bearbeite Favoriten'
    }
  },
  en: {
    translation: {
      cancel: 'Cancel',
      delete: 'Delete',
      'delete-place-header': 'Do you want to delete the place?',
      'delete-place-name': 'Delete place: {{favourite.name}}',
      down: 'Move favourite down',
      'edit-place-name': 'Edit place: {{favourite.name}}',
      'edit-places': 'Edit places',
      up: 'Move favourite up'
    }
  },
  fi: {
    translation: {
      cancel: 'Peruuta',
      delete: 'Poista',
      'delete-place-header': 'Haluatko poistaa paikan?',
      'delete-place-name': 'Poista paikka: {{favourite.name}}',
      down: 'Siirrä suosikkisijaintia alaspäin',
      'edit-place-name': 'Muokkaa paikkaa: {{favourite.name}}',
      'edit-places': 'Muokkaa paikkoja',
      up: 'Siirrä suosikkisijaintia ylöpäin'
    }
  },
  pl: {
    translation: {
      cancel: 'Anuluj',
      delete: 'Usuń',
      'delete-place-header': 'Czy na pewno chcesz usunąć to miejsce?',
      'delete-place-name': 'Usuń miejsce: {{favourite.name}}',
      down: 'Przenieś ulubione niżej',
      'edit-place-name': 'Edytuj miejsce: {{favourite.name}}',
      'edit-places': 'Edytuj miejsca',
      up: 'Przenieś ulubione wyżej'
    }
  },
  sv: {
    translation: {
      cancel: 'Avbryt',
      delete: 'Ta bort',
      'delete-place-header': 'Vill du ta bort platsen?',
      'delete-place-name': 'Ta bort platsen: {{favourite.name}}',
      down: 'Flytta favoriten nedåt',
      'edit-place-name': 'Edit place: {{favourite.name}}',
      'edit-places': 'Redigera platser',
      up: 'Flytta favoriten uppåt'
    }
  }
};

const i18n = createInstance({
  fallbackLng: 'fi',
  // debug: true,
  resources: translations,
  defaultNS: 'translation',
  react: {
    useSuspense: true
  }
});
i18n.init();

const getIconId = selectedIconId => {
  const prefixPos = selectedIconId ? selectedIconId.indexOf('icon_') : -1;
  return prefixPos > 0 ? selectedIconId.substring(prefixPos + 5) : 'place';
};

/**
 * One row of the favourites list. The visually hidden arrow buttons are a
 * keyboard alternative to dragging.
 */
function FavouriteListItem({
  favourite,
  lang,
  onMoveUp = null,
  onMoveDown = null,
  onEdit,
  onDelete,
  index
}) {
  const {
    t
  } = useTranslation('translation', {
    i18n
  });
  const {
    ref
  } = useSortable({
    id: favourite.favouriteId,
    index
  });
  const iconId = getIconId(favourite.selectedIconId);
  const [name, address] = formatFavouritePlaceLabel(favourite.name, favourite.address);
  return /*#__PURE__*/jsxs("li", {
    ref: ref,
    className: styles.item,
    children: [/*#__PURE__*/jsxs("div", {
      className: styles.dragHandle,
      children: [onMoveUp && /*#__PURE__*/jsx("button", {
        type: "button",
        className: styles.arrow,
        "aria-label": t('up', {
          lng: lang
        }),
        onClick: onMoveUp,
        children: "\u2191"
      }), onMoveDown && /*#__PURE__*/jsx("button", {
        type: "button",
        className: styles.arrow,
        "aria-label": t('down', {
          lng: lang
        }),
        onClick: onMoveDown,
        children: "\u2193"
      }), /*#__PURE__*/jsx("span", {
        className: styles.ellipsis,
        children: /*#__PURE__*/jsx(Icon, {
          img: "ellipsis",
          color: "currentColor"
        })
      }), /*#__PURE__*/jsx("span", {
        className: styles.placeIcon,
        children: /*#__PURE__*/jsx(Icon, {
          img: iconId,
          color: "currentColor"
        })
      })]
    }), /*#__PURE__*/jsxs(FlexColumn, {
      className: styles.text,
      children: [/*#__PURE__*/jsx(Text, {
        variant: "text-xs-bold",
        as: "p",
        className: styles.name,
        children: name
      }), /*#__PURE__*/jsx(Text, {
        variant: "text-xs",
        as: "p",
        color: "weak",
        className: styles.address,
        children: address
      })]
    }), /*#__PURE__*/jsxs(FlexRow, {
      alignItems: "center",
      flexShrink: 0,
      children: [/*#__PURE__*/jsx("button", {
        type: "button",
        className: styles.actionButton,
        "aria-label": t('edit-place-name', {
          lng: lang,
          favourite
        }),
        onClick: onEdit,
        children: /*#__PURE__*/jsx(Icon, {
          img: "edit",
          color: "currentColor"
        })
      }), /*#__PURE__*/jsx("button", {
        type: "button",
        className: styles.actionButton,
        "aria-label": t('delete-place-name', {
          lng: lang,
          favourite
        }),
        onClick: onDelete,
        children: /*#__PURE__*/jsx(Icon, {
          img: "trash",
          color: "currentColor"
        })
      })]
    })]
  });
}
process.env.NODE_ENV !== "production" ? FavouriteListItem.propTypes = {
  favourite: PropTypes.shape({
    favouriteId: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    selectedIconId: PropTypes.string
  }).isRequired,
  lang: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  index: PropTypes.number.isRequired
} : void 0;

function FavouriteEditingModal({
  isModalOpen,
  handleClose,
  updateFavourites,
  deleteFavourite,
  onEditSelected,
  favourites,
  isLoading,
  lang = 'fi'
}) {
  const {
    t
  } = useTranslation('translation', {
    i18n
  });
  const [source, setSource] = useState(favourites);
  const [reordered, setReordered] = useState(null);
  const [selectedFavourite, setSelectedFavourite] = useState(null);
  const previousFavourites = useRef(favourites);

  // Drop the local order when the favourites change from outside
  if (!isEqual(source, favourites)) {
    setSource(favourites);
    setReordered(null);
  }
  const items = reordered || favourites;
  useEffect(() => {
    if (previousFavourites.current !== favourites && !isEqual(previousFavourites.current, favourites) && isEmpty(favourites)) {
      handleClose();
    }
    previousFavourites.current = favourites;
  }, [favourites, handleClose]);
  const move = (index, direction) => {
    const copy = [...items];
    [copy[index], copy[index + direction]] = [copy[index + direction], copy[index]];
    setReordered(copy);
  };
  const onDragEnd = event => {
    if (event.canceled) {
      return;
    }
    const {
      initialIndex,
      index
    } = event.operation.source?.sortable || {};
    if (initialIndex === undefined || initialIndex === index) {
      return;
    }
    const copy = [...items];
    const [moved] = copy.splice(initialIndex, 1);
    copy.splice(index, 0, moved);
    setReordered(copy);
  };
  const closeModal = () => {
    handleClose();
    const omitted = items.map(item => omit(item, ['chosen', 'selected']));
    if (!isEqual(omitted, favourites)) {
      updateFavourites(omitted);
    }
  };
  const closeDeleteDialog = () => setSelectedFavourite(null);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(DialogModal, {
      headerText: t('delete-place-header', {
        lng: lang
      }),
      handleClose: () => {
        closeDeleteDialog();
        handleClose();
      },
      isModalOpen: selectedFavourite !== null,
      dialogContent: selectedFavourite ? `${selectedFavourite.name}: ${selectedFavourite.address}` : '',
      primaryButtonText: t('delete', {
        lng: lang
      }),
      primaryButtonOnClick: () => {
        deleteFavourite(selectedFavourite);
        closeDeleteDialog();
      },
      secondaryButtonText: t('cancel', {
        lng: lang
      }),
      secondaryButtonOnClick: closeDeleteDialog,
      lang: lang
    }), /*#__PURE__*/jsx(Modal, {
      open: isModalOpen && selectedFavourite === null,
      onOpenChange: open => {
        if (!open) {
          closeModal();
        }
      },
      children: /*#__PURE__*/jsx(ModalContent, {
        lang: lang,
        title: t('edit-places', {
          lng: lang
        }),
        className: styles.modalContent,
        "aria-describedby": undefined,
        children: /*#__PURE__*/jsxs("div", {
          className: styles.listContainer,
          children: [/*#__PURE__*/jsx(DragDropProvider, {
            onDragEnd: onDragEnd,
            children: /*#__PURE__*/jsx("ul", {
              className: styles.list,
              children: items.map((favourite, index) => /*#__PURE__*/jsx(FavouriteListItem, {
                index: index,
                favourite: favourite,
                lang: lang,
                onMoveUp: index > 0 ? () => move(index, -1) : null,
                onMoveDown: index < items.length - 1 ? () => move(index, 1) : null,
                onEdit: () => onEditSelected(favourite),
                onDelete: () => setSelectedFavourite(favourite)
              }, favourite.favouriteId))
            })
          }), isLoading && /*#__PURE__*/jsx("div", {
            className: styles.overlay,
            children: /*#__PURE__*/jsx(Spinner, {})
          })]
        })
      })
    })]
  });
}
process.env.NODE_ENV !== "production" ? FavouriteEditingModal.propTypes = {
  /** Required. Whether the dialog is open.
   * @type {boolean} */
  isModalOpen: PropTypes.bool.isRequired,
  /** Required. Close modal.
   * @type {function} */
  handleClose: PropTypes.func.isRequired,
  /** Required. Called with the reordered favourites when the dialog is closed.
   * @type {function} */
  updateFavourites: PropTypes.func.isRequired,
  /** Required. Called with the favourite the user confirmed deleting.
   * @type {function} */
  deleteFavourite: PropTypes.func.isRequired,
  /** Required. Function that takes selected favourite object as parameter.
   * @type {function} */
  onEditSelected: PropTypes.func.isRequired,
  /** Required. Whether a save is in progress; shows a spinner over the list.
   * @type {boolean} */
  isLoading: PropTypes.bool.isRequired,
  /** Required.
   * @type {array<object>}
   * @property {string} type
   * @property {string} address
   * @property {string} gtfsId
   * @property {string} gid
   * @property {number} lat
   * @property {number} lon
   * @property {string} name
   * @property {string} selectedIconId
   * @property {string} favouriteId
   * @property {string} layer
   */
  favourites: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    address: PropTypes.string,
    gtfsId: PropTypes.string,
    gid: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    name: PropTypes.string,
    selectedIconId: PropTypes.string,
    favouriteId: PropTypes.string,
    layer: PropTypes.string
  })).isRequired,
  /** Optional. Language, fi, en or sv. Defaults to fi.
   * @type {string} */
  lang: PropTypes.string
} : void 0;

export { FavouriteEditingModal as default };
//# sourceMappingURL=index.js.map
